import { z } from 'zod';
import { validateToolInputSchema } from '../tool-schema-validator';
import { withinJsonLength } from './common';
import { SLUG_REGEX } from './setup/slug';
import {
  BINDING_EACH_MAX_DEPTH,
  BINDING_EACH_MAX_ITEMS,
  BINDING_LOOP_NAMES,
  BINDING_NAME_SEGMENT_REGEX,
  BINDING_RENDER_MAX_STEPS,
  BINDING_ROOTS,
  BINDING_VALUE_MAX_DEPTH,
  type BindingBudget,
  type BindingContext,
  type BindingFrame,
  type BindingScope,
  bindingEachItems,
  bindingScope,
  bindingFormatterArgError,
  bindingTextEachDepth,
  collectBindingReferences,
  evaluateBindingPath,
  isBindingTruthy,
  isPlainRecord,
  parseBindingPath,
  parseBindingText,
  renderBindingText,
  resolveBindingValue,
  wholeBindingExpr,
} from './interactive-bindings';

/**
 * Interactive templates (wayai-pro/wayai#4751 Phase 1): a builder's component, composed from WayAI's
 * fixed building blocks (decision 1), stored as hub config (decision 4) and sent by the runtime agent
 * with its declared params. A message that shows one carries `{ kind: 'template', template, version,
 * params, data, status }` (`interactive.ts`): the version it was sent with is the one it renders and
 * the one the server checks its actions against (decision 8).
 *
 * A DEFINITION (`interactiveTemplateDefinitionSchema`) is:
 *   - `name`: a slug, immutable like a state slug; never one of the built-ins' names;
 *   - `description`: what the agent reads to pick it;
 *   - `params`: a JSON Schema (root `type: object`) of what the agent passes;
 *   - `data` (optional): the tool the backend calls for it (`tool`), the values it calls it with
 *     (`params`: a list of the template's own param names, passed through, or a map of value
 *     templates over `params.*` and `state.*`), and the top-level `fields` of its result the template
 *     reads, which are all the message's data snapshot keeps;
 *   - `views.main`: the one view (several are Phase 1b's);
 *   - `actions`: named, referenced by a `button`'s `action` or a `form`'s `submit`. Phase 1's are `send`
 *     (text and/or values → a normal new user turn, decision 7) and `set_state`, `append_state`,
 *     `remove_state` (a `state.<slug>…` path and a value template). Phase 1 fixes each type's notify
 *     level instead of carrying `notify` (`INTERACTIVE_TEMPLATE_ACTION_NOTIFY`); Phase 1b adds the
 *     per-action field with its `none` mode;
 *   - `fallback`: required text, rendered into `message_text` for every surface that does not draw it.
 *
 * Text props, `fallback` and the strings in value templates use the binding language
 * (`interactive-bindings.ts`); `each` (a `list`'s) and `if` (any block's) take a bare path.
 *
 * CHECKING. The schema is the shape; `validateInteractiveTemplateDefinition` is the whole structural
 * check (shape, caps, bindings that resolve to declared params, data fields or `state.` paths, the
 * actions, the fallback) and the one every writer calls. Whether a named state or tool exists in a hub
 * needs the hub and is its caller's.
 *
 * EVOLUTION. Definitions are strict: a reader meeting a field, block type or action type it does not
 * know refuses the whole definition, and the client renders the message's `message_text`. A definition
 * using a later feature thus degrades to its fallback on an older app, never to a partial render.
 *
 * RENDERING. `resolveInteractiveTemplateView` turns a definition plus its message's params, data and
 * the live state into a tree of resolved blocks (`ResolvedTemplateBlock`): text filled, lists
 * repeated, hidden blocks dropped, every value typed. Renderers draw that tree and nothing else. A
 * button or form carries the ADDRESS of its action (`{ site, indices, keys }`), never the action:
 * `resolveInteractiveTemplateAction` resolves an address against the stored version, server-side.
 */

// =============================================================================
// CAPS
// =============================================================================

/**
 * The most JSON text one definition takes, in UTF-16 code units (`JSON.stringify(definition).length`),
 * not bytes: at most 192 KiB of UTF-8 (3 bytes per unit, worst case).
 */
export const INTERACTIVE_TEMPLATE_MAX_CHARS = 64 * 1024;
/** The most blocks one view holds (a list's `item` counts once). */
export const INTERACTIVE_TEMPLATE_MAX_BLOCKS = 200;
/** The most levels blocks nest. */
export const INTERACTIVE_TEMPLATE_MAX_DEPTH = 12;
/** The most actions one definition declares. */
export const INTERACTIVE_TEMPLATE_MAX_ACTIONS = 30;
/** The most one text prop takes, in UTF-16 code units (at most 6 KiB of UTF-8). */
export const INTERACTIVE_TEMPLATE_TEXT_MAX = 2000;
/** The most the fallback template takes, in UTF-16 code units (at most 12 KiB of UTF-8). */
export const INTERACTIVE_TEMPLATE_FALLBACK_MAX = 4000;
export const INTERACTIVE_TEMPLATE_DESCRIPTION_MAX = 500;
/** The most JSON text the `params` schema takes, in UTF-16 code units (at most 48 KiB of UTF-8). */
export const INTERACTIVE_TEMPLATE_PARAMS_SCHEMA_MAX_CHARS = 16 * 1024;
/** The most JSON text one action's value template takes, in UTF-16 code units (at most 12 KiB of UTF-8). */
export const INTERACTIVE_TEMPLATE_VALUE_MAX_CHARS = 4 * 1024;
/** The most data fields a definition reads, and the most data params it passes. */
export const INTERACTIVE_TEMPLATE_DATA_FIELDS_MAX = 20;
/** The most literal options a `select` lists. */
export const INTERACTIVE_TEMPLATE_OPTIONS_MAX = 50;
/** The most resolved blocks one render produces; a view needing more renders as its fallback. */
export const INTERACTIVE_TEMPLATE_RENDER_MAX_BLOCKS = 2000;
const PATH_MAX = 256;
const TOOL_NAME_MAX = 128;
/** The most JSON nesting a definition may have before its shape is even read. */
const RAW_MAX_DEPTH = 64;

/** A template's name, an action's name: a slug, like a state's (`order_summary`). */
export const interactiveTemplateNameSchema = z.string().regex(SLUG_REGEX, {
  message: 'a template name is lowercase letters, digits and underscores, starting with a letter (e.g. "menu")',
});
const actionNameSchema = z.string().regex(SLUG_REGEX, {
  message: 'an action name is lowercase letters, digits and underscores, starting with a letter',
});
/** A resolved form field's name: what its value is keyed by in the submitted values. */
export const INTERACTIVE_TEMPLATE_FIELD_NAME_REGEX = /^[A-Za-z_][A-Za-z0-9_]{0,63}$/;

/** The roots a data field may not be named, since a bare path reaching it would mean them. */
const RESERVED_FIELD_NAMES: ReadonlySet<string> = new Set(BINDING_ROOTS);

// =============================================================================
// BUILDING BLOCKS
// =============================================================================

export const TEMPLATE_TONES = ['default', 'secondary', 'success', 'danger', 'warning', 'info'] as const;
export type TemplateTone = (typeof TEMPLATE_TONES)[number];
export const TEMPLATE_INPUT_TYPES = ['text', 'number', 'email', 'tel', 'date'] as const;
export type TemplateInputType = (typeof TEMPLATE_INPUT_TYPES)[number];
export const TEMPLATE_ALIGNS = ['start', 'center', 'end', 'stretch'] as const;
export const TEMPLATE_JUSTIFIES = ['start', 'center', 'end', 'between'] as const;
export const TEMPLATE_TITLE_SIZES = ['sm', 'md', 'lg'] as const;
export const TEMPLATE_TEXT_SIZES = ['xs', 'sm', 'md', 'lg'] as const;
export const TEMPLATE_TEXT_WEIGHTS = ['normal', 'medium', 'bold'] as const;
export const TEMPLATE_BADGE_VARIANTS = ['solid', 'soft', 'outline'] as const;
export const TEMPLATE_BUTTON_STYLES = ['primary', 'secondary'] as const;
export const TEMPLATE_LIST_DIRECTIONS = ['vertical', 'horizontal'] as const;
type OneOf<T extends readonly string[]> = T[number];

/** The Phase 1 building blocks (decision 1); chart blocks are Phase 1b's. */
export const TEMPLATE_BLOCK_TYPES = [
  'card', 'row', 'col', 'list', 'image', 'title', 'text', 'badge', 'divider',
  'button', 'select', 'input', 'checkbox', 'form',
] as const;
export type TemplateBlockType = (typeof TEMPLATE_BLOCK_TYPES)[number];

/** A text prop: literal text and bindings. */
type Text = string;
/** A bare path: a `list`'s `each`, a block's `if`. */
type Path = string;
/** One whole `{{binding}}`, read as its value (a list, a boolean), or the literal it stands for. */
type Bound<T> = T | string;

interface BlockCommon {
  /** Shown only when this path is truthy. */
  if?: Path;
}
interface ContainerProps {
  gap?: number;
  align?: (typeof TEMPLATE_ALIGNS)[number];
  justify?: (typeof TEMPLATE_JUSTIFIES)[number];
}
interface FieldCommon extends BlockCommon {
  /** The key the field's value is submitted under: literal, or bound for a field built from params. */
  name: Text;
  label?: Text;
  required?: Bound<boolean>;
  /** Where the value came from, shown under the field ("verified from the CNPJ lookup"). */
  source?: Text;
}
export interface TemplateOption {
  value: Text;
  label: Text;
}

export type TemplateBlock =
  | (BlockCommon & { type: 'card'; children: TemplateBlock[] })
  | (BlockCommon & ContainerProps & { type: 'row' | 'col'; children: TemplateBlock[] })
  | (BlockCommon & {
      type: 'list';
      each: Path;
      item: TemplateBlock;
      direction?: OneOf<typeof TEMPLATE_LIST_DIRECTIONS>;
      limit?: number;
      /**
       * The element field that identifies an element (`sku`). Required on a list whose elements can
       * change between render and tap (one over `state.*`, or nested in such a list), so a tap acts on
       * the element it was made on (TemplateActionAddress).
       */
      key?: string;
      /** Shown when the list has no elements. */
      empty?: Text;
    })
  | (BlockCommon & { type: 'image'; src: Text; alt?: Text })
  | (BlockCommon & { type: 'title'; text: Text; size?: OneOf<typeof TEMPLATE_TITLE_SIZES> })
  | (BlockCommon & {
      type: 'text';
      text: Text;
      size?: OneOf<typeof TEMPLATE_TEXT_SIZES>;
      weight?: OneOf<typeof TEMPLATE_TEXT_WEIGHTS>;
      color?: TemplateTone;
    })
  | (BlockCommon & { type: 'badge'; text: Text; color?: TemplateTone; variant?: OneOf<typeof TEMPLATE_BADGE_VARIANTS> })
  | (BlockCommon & { type: 'divider' })
  | (BlockCommon & { type: 'button'; label: Text; action: string; style?: OneOf<typeof TEMPLATE_BUTTON_STYLES> })
  | (FieldCommon & {
      type: 'select';
      options: Bound<TemplateOption[]>;
      placeholder?: Text;
      default_value?: Text;
    })
  | (FieldCommon & {
      type: 'input';
      input_type?: Bound<TemplateInputType>;
      placeholder?: Text;
      default_value?: Text;
    })
  | (FieldCommon & { type: 'checkbox'; label: Text; default_checked?: Bound<boolean> })
  | (BlockCommon & { type: 'form'; children: TemplateBlock[]; submit: string; submit_label?: Text });

const text = z.string().max(INTERACTIVE_TEMPLATE_TEXT_MAX);
const label = text.min(1);
const path = z.string().min(1).max(PATH_MAX);
const binding = z.string().min(1).max(PATH_MAX);
const common = { if: path.optional() };
const fieldCommon = {
  ...common,
  name: label,
  label: text.optional(),
  required: z.union([z.boolean(), binding]).optional(),
  source: text.optional(),
};
const children = () => z.array(blockSchema).min(1).max(INTERACTIVE_TEMPLATE_MAX_BLOCKS);
const containerProps = () => ({
  gap: z.number().int().min(0).max(8).optional(),
  align: z.enum(TEMPLATE_ALIGNS).optional(),
  justify: z.enum(TEMPLATE_JUSTIFIES).optional(),
});
const optionSchema = z.object({ value: text, label }).strict();

/** One building block (`TemplateBlock`), recursively. Strict: an unknown prop refuses the block. */
export const templateBlockSchema: z.ZodType<TemplateBlock> = z.lazy(() =>
  z.discriminatedUnion('type', [
    z.object({ ...common, type: z.literal('card'), children: children() }).strict(),
    z.object({ ...common, ...containerProps(), type: z.literal('row'), children: children() }).strict(),
    z.object({ ...common, ...containerProps(), type: z.literal('col'), children: children() }).strict(),
    z.object({
      ...common,
      type: z.literal('list'),
      each: path,
      item: blockSchema,
      direction: z.enum(TEMPLATE_LIST_DIRECTIONS).optional(),
      limit: z.number().int().min(1).max(BINDING_EACH_MAX_ITEMS).optional(),
      key: z.string().max(64).regex(BINDING_NAME_SEGMENT_REGEX).optional(),
      empty: text.optional(),
    }).strict(),
    z.object({ ...common, type: z.literal('image'), src: label, alt: text.optional() }).strict(),
    z.object({ ...common, type: z.literal('title'), text: label, size: z.enum(TEMPLATE_TITLE_SIZES).optional() }).strict(),
    z.object({
      ...common,
      type: z.literal('text'),
      text: label,
      size: z.enum(TEMPLATE_TEXT_SIZES).optional(),
      weight: z.enum(TEMPLATE_TEXT_WEIGHTS).optional(),
      color: z.enum(TEMPLATE_TONES).optional(),
    }).strict(),
    z.object({
      ...common,
      type: z.literal('badge'),
      text: label,
      color: z.enum(TEMPLATE_TONES).optional(),
      variant: z.enum(TEMPLATE_BADGE_VARIANTS).optional(),
    }).strict(),
    z.object({ ...common, type: z.literal('divider') }).strict(),
    z.object({
      ...common,
      type: z.literal('button'),
      label,
      action: actionNameSchema,
      style: z.enum(TEMPLATE_BUTTON_STYLES).optional(),
    }).strict(),
    z.object({
      ...fieldCommon,
      type: z.literal('select'),
      options: z.union([z.array(optionSchema).min(1).max(INTERACTIVE_TEMPLATE_OPTIONS_MAX), binding]),
      placeholder: text.optional(),
      default_value: text.optional(),
    }).strict(),
    z.object({
      ...fieldCommon,
      type: z.literal('input'),
      input_type: z.union([z.enum(TEMPLATE_INPUT_TYPES), binding]).optional(),
      placeholder: text.optional(),
      default_value: text.optional(),
    }).strict(),
    z.object({
      ...fieldCommon,
      type: z.literal('checkbox'),
      label,
      default_checked: z.union([z.boolean(), binding]).optional(),
    }).strict(),
    z.object({
      ...common,
      type: z.literal('form'),
      children: children(),
      submit: actionNameSchema,
      submit_label: text.optional(),
    }).strict(),
  ]),
) as z.ZodType<TemplateBlock>;
const blockSchema = templateBlockSchema;

// =============================================================================
// ACTIONS
// =============================================================================

export const TEMPLATE_ACTION_TYPES = ['send', 'set_state', 'append_state', 'remove_state'] as const;
export type TemplateActionType = (typeof TEMPLATE_ACTION_TYPES)[number];

/**
 * What the agent hears of each Phase 1 action (decision 5's levels). Fixed per type until Phase 1b's
 * per-action `notify`: a `send` is a normal new user turn (decision 7); a state edit is written by the
 * backend and reaches the next turn as its readable summary.
 */
export const INTERACTIVE_TEMPLATE_ACTION_NOTIFY = {
  send: 'turn',
  set_state: 'summary',
  append_state: 'summary',
  remove_state: 'summary',
} as const satisfies Record<TemplateActionType, 'none' | 'summary' | 'turn'>;

/** `state.<slug>` then field names: where a state action writes (`state.order.items`). */
const unanchored = (regex: RegExp) => regex.source.replace(/^\^/, '').replace(/\$$/, '');
export const INTERACTIVE_TEMPLATE_STATE_PATH_REGEX = new RegExp(
  `^state\\.${unanchored(SLUG_REGEX)}(\\.${unanchored(BINDING_NAME_SEGMENT_REGEX)}){0,10}$`,
);

const valueTemplate = z.unknown().superRefine((value, ctx) => {
  if (!withinJsonLength(value, INTERACTIVE_TEMPLATE_VALUE_MAX_CHARS)) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: `a value is JSON of at most ${INTERACTIVE_TEMPLATE_VALUE_MAX_CHARS} characters` });
  }
});
const statePath = z.string().regex(INTERACTIVE_TEMPLATE_STATE_PATH_REGEX, {
  message: 'a state path is "state.<state slug>" then field names (e.g. "state.order.items")',
});

export type TemplateAction =
  | { type: 'send'; text?: Text; values?: unknown }
  | { type: 'set_state' | 'append_state' | 'remove_state'; path: string; value: unknown };

/**
 * One action. `set_state` replaces the value at `path`; `append_state` adds `value` to the list at
 * `path`; `remove_state` removes from that list every element matching `value` (a scalar: equal; an
 * object: every key it names equal).
 */
export const templateActionSchema: z.ZodType<TemplateAction> = z.discriminatedUnion('type', [
  z.object({ type: z.literal('send'), text: label.optional(), values: valueTemplate.optional() }).strict(),
  z.object({ type: z.literal('set_state'), path: statePath, value: valueTemplate }).strict(),
  z.object({ type: z.literal('append_state'), path: statePath, value: valueTemplate }).strict(),
  z.object({ type: z.literal('remove_state'), path: statePath, value: valueTemplate }).strict(),
]).superRefine((action, ctx) => {
  if (action.type === 'send' && action.text === undefined && action.values === undefined) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'a send action has text, values, or both' });
  }
}) as z.ZodType<TemplateAction>;

// =============================================================================
// DEFINITION
// =============================================================================

const paramsJsonSchema = z.record(z.unknown()).superRefine((schema, ctx) => {
  if (!withinJsonLength(schema, INTERACTIVE_TEMPLATE_PARAMS_SCHEMA_MAX_CHARS)) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: `params is at most ${INTERACTIVE_TEMPLATE_PARAMS_SCHEMA_MAX_CHARS} characters of JSON` });
    return;
  }
  const result = validateToolInputSchema(schema);
  if (!result.ok) {
    for (const error of result.errors) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `params is not a valid JSON Schema${error.path ? ` at "${error.path}"` : ''}: ${error.message}`,
      });
    }
  }
});

const dataSchema = z.object({
  /** The tool's name, as the agent holds it. The backend calls it; the model never types its data. */
  tool: z.string().trim().min(1).max(TOOL_NAME_MAX),
  params: z
    .union([
      z.array(z.string()).max(INTERACTIVE_TEMPLATE_DATA_FIELDS_MAX),
      z.record(valueTemplate).refine((r) => Object.keys(r).length <= INTERACTIVE_TEMPLATE_DATA_FIELDS_MAX, {
        message: `data.params passes at most ${INTERACTIVE_TEMPLATE_DATA_FIELDS_MAX} values`,
      }),
    ])
    .default({})
    .transform((params): Record<string, unknown> =>
      Array.isArray(params) ? Object.fromEntries(params.map((name) => [name, `{{params.${name}}}`])) : params,
    ),
  /** The result's top-level fields the template reads: what the message's data snapshot keeps. */
  fields: z.array(z.string().max(64).regex(BINDING_NAME_SEGMENT_REGEX)).min(1).max(INTERACTIVE_TEMPLATE_DATA_FIELDS_MAX),
}).strict();

/** A template definition as a builder writes it (`z.input`) and as stored (`z.output`). */
export const interactiveTemplateDefinitionSchema = z.object({
  name: interactiveTemplateNameSchema,
  description: z.string().trim().min(1).max(INTERACTIVE_TEMPLATE_DESCRIPTION_MAX),
  params: paramsJsonSchema.default({ type: 'object', properties: {} }),
  data: dataSchema.optional(),
  /** Phase 1: the one view, `main`. Phase 1b adds others beside it. */
  views: z.object({ main: blockSchema }).strict(),
  actions: z
    .record(actionNameSchema, templateActionSchema)
    .default({})
    .refine((actions) => Object.keys(actions).length <= INTERACTIVE_TEMPLATE_MAX_ACTIONS, {
      message: `at most ${INTERACTIVE_TEMPLATE_MAX_ACTIONS} actions`,
    }),
  fallback: z.string().trim().min(1).max(INTERACTIVE_TEMPLATE_FALLBACK_MAX),
}).strict();

export type InteractiveTemplateDefinitionInput = z.input<typeof interactiveTemplateDefinitionSchema>;
export type InteractiveTemplateDefinition = z.output<typeof interactiveTemplateDefinitionSchema>;

// =============================================================================
// VALIDATION
// =============================================================================

export interface InteractiveTemplateIssue {
  /** Where, as a dotted path into the definition (`views.main.children.0.text`); empty for the whole. */
  path: string;
  message: string;
}

export type InteractiveTemplateValidation =
  | { ok: true; definition: InteractiveTemplateDefinition }
  | { ok: false; issues: InteractiveTemplateIssue[] };

/** The deepest nesting of `value`, counted without recursion, stopping past `max`. */
function jsonDepthExceeds(value: unknown, max: number): boolean {
  const stack: Array<[unknown, number]> = [[value, 1]];
  while (stack.length > 0) {
    const [node, depth] = stack.pop()!;
    if (node === null || typeof node !== 'object') continue;
    if (depth > max) return true;
    for (const child of Object.values(node)) stack.push([child, depth + 1]);
  }
  return false;
}

/** The form a field sits in: the static names seen so far, and the `each` depth of the form itself. */
interface FormCheck {
  names: Set<string>;
  eachDepth: number;
}

interface CheckScope {
  /** `each` levels around the binding: `list` blocks and `{{#each}}` both count. */
  eachDepth: number;
  /** The binding sits in an action a form submits, so `form.*` reads the submitted values. */
  formValues: boolean;
  /** The binding is a `data.params` value: data is not loaded yet, so only params and state exist. */
  beforeData: boolean;
}

class Checker {
  readonly issues: InteractiveTemplateIssue[] = [];
  readonly paramNames: Set<string>;
  readonly dataFields: Set<string> | null;
  blocks = 0;

  constructor(readonly definition: InteractiveTemplateDefinition) {
    const properties = definition.params.properties;
    this.paramNames = new Set(
      properties && typeof properties === 'object' && !Array.isArray(properties) ? Object.keys(properties) : [],
    );
    this.dataFields = definition.data ? new Set(definition.data.fields) : null;
  }

  issue(path: string, message: string): void {
    this.issues.push({ path, message });
  }

  /** Whether one parsed path resolves to something the definition declares, at this scope. */
  checkPath(segments: readonly string[], where: string, scope: CheckScope): void {
    const [head, second] = segments;
    const shown = segments.join('.');
    const inEach = scope.eachDepth > 0;
    if (scope.beforeData && head !== 'params' && head !== 'state') {
      this.issue(where, `"${shown}": data params read only params.* and state.* (the data is not loaded yet)`);
      return;
    }
    if ((BINDING_LOOP_NAMES as readonly string[]).includes(head ?? '')) {
      if (!inEach) this.issue(where, `"${shown}" is read only inside an each (a list's item or {{#each}})`);
      return;
    }
    switch (head) {
      case 'item':
      case 'this':
        if (!inEach) this.issue(where, `"${shown}" is read only inside an each (a list's item or {{#each}})`);
        return;
      case 'params':
        if (second !== undefined && !this.paramNames.has(second)) {
          this.issue(where, `"${shown}": "${second}" is not a declared param (params.properties)`);
        }
        return;
      case 'state':
        if (second === undefined || !SLUG_REGEX.test(second)) {
          this.issue(where, `"${shown}": a state path is state.<state slug>…`);
        }
        return;
      case 'form':
        if (!scope.formValues) this.issue(where, `"${shown}": form.* is read only by the action a form submits`);
        return;
      case 'data':
        if (!this.dataFields) this.issue(where, `"${shown}": the template declares no data`);
        else if (second !== undefined && !this.dataFields.has(second)) {
          this.issue(where, `"${shown}": "${second}" is not in data.fields`);
        }
        return;
      default:
        // Inside an each a bare name may be the element's own field, which nothing declares.
        if (inEach) return;
        if (!this.dataFields) this.issue(where, `"${shown}": not a param, state or data field (the template declares no data)`);
        else if (head === undefined || !this.dataFields.has(head)) {
          this.issue(where, `"${shown}": "${head}" is not in data.fields (params and states are read as params.* and state.*)`);
        }
    }
  }

  checkText(source: string, where: string, scope: CheckScope): void {
    const parsed = parseBindingText(source);
    if (!parsed.ok) {
      this.issue(where, parsed.error);
      return;
    }
    if (scope.eachDepth + bindingTextEachDepth(parsed.value) > BINDING_EACH_MAX_DEPTH) {
      this.issue(where, `each nests more than ${BINDING_EACH_MAX_DEPTH} levels (lists and {{#each}} together)`);
      return;
    }
    for (const ref of collectBindingReferences(parsed.value)) {
      this.checkPath(ref.path, where, { ...scope, eachDepth: scope.eachDepth + ref.eachDepth });
      for (const call of ref.formatters) {
        const error = bindingFormatterArgError(call.name, call.arg);
        if (error) this.issue(where, error);
      }
    }
  }

  checkBarePath(source: string, where: string, scope: CheckScope): void {
    const parsed = parseBindingPath(source);
    if (!parsed.ok) this.issue(where, parsed.error);
    else this.checkPath(parsed.value, where, scope);
  }

  checkWholeBinding(source: string, where: string, scope: CheckScope): void {
    const expr = wholeBindingExpr(source);
    if (!expr) {
      this.issue(where, 'expected one whole binding, e.g. "{{item.options}}"');
      return;
    }
    this.checkText(source, where, scope);
  }

  /** A value template: its strings' bindings, and the shape resolution reads whole (no silent cut). */
  checkValue(value: unknown, where: string, scope: CheckScope, depth = 0): void {
    if (typeof value === 'string') {
      this.checkText(value, where, scope);
      return;
    }
    if (value === null || typeof value !== 'object') return;
    if (depth >= BINDING_VALUE_MAX_DEPTH) {
      this.issue(where, `a value nests more than ${BINDING_VALUE_MAX_DEPTH} levels`);
      return;
    }
    if (Array.isArray(value)) {
      if (value.length > BINDING_EACH_MAX_ITEMS) this.issue(where, `a value list has at most ${BINDING_EACH_MAX_ITEMS} entries`);
      value.forEach((entry, i) => this.checkValue(entry, `${where}.${i}`, scope, depth + 1));
    } else {
      for (const [key, entry] of Object.entries(value)) this.checkValue(entry, `${where}.${key}`, scope, depth + 1);
    }
  }

  checkAction(name: string, where: string, eachDepth: number, formValues: boolean): void {
    const action = this.definition.actions[name];
    if (!action) {
      this.issue(where, `"${name}" is not a declared action`);
      return;
    }
    const at = `actions.${name}`;
    const scope: CheckScope = { eachDepth, formValues, beforeData: false };
    if (action.type === 'send') {
      if (action.text !== undefined) this.checkText(action.text, `${at}.text`, scope);
      if (action.values !== undefined) this.checkValue(action.values, `${at}.values`, scope);
    } else {
      this.checkValue(action.value, `${at}.value`, scope);
    }
  }

  /** `live`: the block sits in a list whose elements can change between render and tap. */
  checkBlock(block: TemplateBlock, where: string, depth: number, eachDepth: number, inForm: FormCheck | null, live = false): void {
    this.blocks += 1;
    if (depth > INTERACTIVE_TEMPLATE_MAX_DEPTH) {
      this.issue(where, `blocks nest more than ${INTERACTIVE_TEMPLATE_MAX_DEPTH} levels`);
      return;
    }
    const scope: CheckScope = { eachDepth, formValues: false, beforeData: false };
    if (block.if !== undefined) this.checkBarePath(block.if, `${where}.if`, scope);
    const texts = (props: object, keys: string[]) => {
      for (const key of keys) {
        const value = (props as Record<string, unknown>)[key];
        if (typeof value === 'string') this.checkText(value, `${where}.${key}`, scope);
      }
    };
    const bound = (value: unknown, key: string) => {
      if (typeof value === 'string') this.checkWholeBinding(value, `${where}.${key}`, scope);
    };
    switch (block.type) {
      case 'card':
      case 'row':
      case 'col':
        block.children.forEach((child, i) => this.checkBlock(child, `${where}.children.${i}`, depth + 1, eachDepth, inForm, live));
        return;
      case 'form':
        if (inForm) this.issue(where, 'a form cannot sit inside another form');
        texts(block, ['submit_label']);
        this.checkAction(block.submit, `${where}.submit`, eachDepth, true);
        {
          const form: FormCheck = { names: new Set(), eachDepth };
          block.children.forEach((child, i) => this.checkBlock(child, `${where}.children.${i}`, depth + 1, eachDepth, form, live));
        }
        return;
      case 'list':
        if (eachDepth + 1 > BINDING_EACH_MAX_DEPTH) {
          this.issue(where, `lists nest more than ${BINDING_EACH_MAX_DEPTH} levels`);
          return;
        }
        this.checkBarePath(block.each, `${where}.each`, scope);
        texts(block, ['empty']);
        {
          // Live: over state, or over an element (or bare name) of a live list. params and the data
          // snapshot are fixed per message, so their elements keep their index.
          const root = block.each.split('.')[0] ?? '';
          const listLive = root === 'state' || (live && root !== 'params' && root !== 'data');
          if (listLive && block.key === undefined) {
            this.issue(`${where}.key`, 'a list over live state names a key (an element field, e.g. "sku"), so a tap acts on the element it was made on');
          }
          this.checkBlock(block.item, `${where}.item`, depth + 1, eachDepth + 1, inForm, listLive);
        }
        return;
      case 'image':
        texts(block, ['src', 'alt']);
        return;
      case 'title':
      case 'text':
      case 'badge':
        texts(block, ['text']);
        return;
      case 'divider':
        return;
      case 'button':
        texts(block, ['label']);
        this.checkAction(block.action, `${where}.action`, eachDepth, false);
        return;
      case 'select':
      case 'input':
      case 'checkbox':
        if (!inForm) this.issue(where, `a ${block.type} sits inside a form, whose submit sends its value`);
        texts(block, ['name', 'label', 'source', 'placeholder', 'default_value']);
        bound(block.required, 'required');
        if (block.type === 'select') {
          if (typeof block.options === 'string') bound(block.options, 'options');
          else {
            block.options.forEach((option, i) => {
              this.checkText(option.value, `${where}.options.${i}.value`, scope);
              this.checkText(option.label, `${where}.options.${i}.label`, scope);
            });
          }
        }
        if (block.type === 'input') bound(block.input_type, 'input_type');
        if (block.type === 'checkbox') bound(block.default_checked, 'default_checked');
        if (inForm && !block.name.includes('{{')) {
          if (!INTERACTIVE_TEMPLATE_FIELD_NAME_REGEX.test(block.name)) {
            this.issue(`${where}.name`, 'a field name is letters, digits and underscores, starting with a letter or underscore');
          } else if (eachDepth > inForm.eachDepth) {
            this.issue(`${where}.name`, 'a field repeated by a list takes a bound name (e.g. "{{item.name}}"), or its copies share one');
          } else if (inForm.names.has(block.name)) {
            this.issue(`${where}.name`, `two fields of one form are named "${block.name}"`);
          }
          inForm.names.add(block.name);
        }
        return;
    }
  }
}

function zodIssues(error: z.ZodError): InteractiveTemplateIssue[] {
  return error.issues.map((issue) => ({ path: issue.path.join('.'), message: issue.message }));
}

/**
 * The structural check of a definition, shared by every writer (HubDO's write, the CLI's push):
 * shape, caps, bindings, actions and fallback, without the hub (TOP: CHECKING). `builtin` is for
 * WayAI's own built-ins, whose names a hub's templates may not take. Never throws.
 */
export function validateInteractiveTemplateDefinition(
  input: unknown,
  options: { builtin?: boolean } = {},
): InteractiveTemplateValidation {
  try {
    if (!withinJsonLength(input, INTERACTIVE_TEMPLATE_MAX_CHARS) || jsonDepthExceeds(input, RAW_MAX_DEPTH)) {
      return { ok: false, issues: [{ path: '', message: `a template is JSON of at most ${INTERACTIVE_TEMPLATE_MAX_CHARS} characters and ${RAW_MAX_DEPTH} levels` }] };
    }
    const parsed = interactiveTemplateDefinitionSchema.safeParse(input);
    if (!parsed.success) return { ok: false, issues: zodIssues(parsed.error) };
    const definition = parsed.data;
    const checker = new Checker(definition);

    if (!options.builtin && INTERACTIVE_BUILTIN_TEMPLATE_NAMES.has(definition.name)) {
      checker.issue('name', `"${definition.name}" is a built-in template's name`);
    }
    if (definition.data) {
      definition.data.fields.forEach((field, i) => {
        if (RESERVED_FIELD_NAMES.has(field)) checker.issue(`data.fields.${i}`, `"${field}" is a reserved name`);
      });
      const dataScope: CheckScope = { eachDepth: 0, formValues: false, beforeData: true };
      for (const [name, value] of Object.entries(definition.data.params)) {
        // Depth 1: `resolveInteractiveTemplateDataParams` resolves the whole record, which is depth 0.
        checker.checkValue(value, `data.params.${name}`, dataScope, 1);
      }
    }
    checker.checkBlock(definition.views.main, 'views.main', 1, 0, null);
    if (checker.blocks > INTERACTIVE_TEMPLATE_MAX_BLOCKS) {
      checker.issue('views.main', `a view holds at most ${INTERACTIVE_TEMPLATE_MAX_BLOCKS} blocks`);
    }
    const referenced = new Set<string>();
    collectActionNames(definition.views.main, referenced);
    for (const name of Object.keys(definition.actions)) {
      if (!referenced.has(name)) checker.issue(`actions.${name}`, `"${name}" is declared but no button or form uses it`);
    }
    checker.checkText(definition.fallback, 'fallback', { eachDepth: 0, formValues: false, beforeData: false });

    return checker.issues.length > 0 ? { ok: false, issues: checker.issues } : { ok: true, definition };
  } catch {
    return { ok: false, issues: [{ path: '', message: 'the template could not be read' }] };
  }
}

function collectActionNames(block: TemplateBlock, out: Set<string>): void {
  if (block.type === 'button') out.add(block.action);
  if (block.type === 'form') out.add(block.submit);
  if ('children' in block) for (const child of block.children) collectActionNames(child, out);
  if (block.type === 'list') collectActionNames(block.item, out);
}

// =============================================================================
// BUILT-IN NAMES (the definitions live in interactive-builtins.ts)
// =============================================================================

/** The built-in templates' names (decision 1): no hub template takes one. */
export const INTERACTIVE_BUILTIN_TEMPLATE_NAMES: ReadonlySet<string> = new Set(['choices', 'buttons', 'form']);

// =============================================================================
// RESOLUTION
// =============================================================================

/**
 * Where a button's or form's action lives: the block's `site` in the view (`main.0.item.2`: child 0,
 * then a list's item, then child 2) and, for each list on the way, the element's index and, for a
 * list that declares a `key`, that element's key (`keys`, parallel to `indices`; null for a list
 * without one). The client sends it back as is; the server resolves it against the stored version, so
 * it never takes an action, a state path or a value from the client (decision 8).
 *
 * A keyed list's element is found again BY KEY at tap time: the tap acts on that element's current
 * values (a quantity changed since render included) wherever it now sits, and resolves to nothing
 * unless exactly one element carries the key. An element whose key another element of the list
 * shares at render gets a null key, so it is drawn but its actions resolve to nothing: keep keys
 * unique (one cart line per `sku`, say). (A whole-value fingerprint was the alternative: it needs no key
 * but refuses every tap on an element any of whose fields changed since render.) An unkeyed list is
 * found by index, which the validator allows only where elements cannot change between render and
 * tap: lists over params and the data snapshot.
 */
export interface TemplateActionAddress {
  site: string;
  indices: number[];
  keys: Array<string | null>;
}

/**
 * An element's key: its `key` field, when a string or a finite number, as JSON text (so `1` and `"1"`
 * stay apart). Null when the element has none.
 */
function elementKey(element: unknown, key: string | undefined): string | null {
  if (key === undefined || !isPlainRecord(element)) return null;
  const value = Object.prototype.hasOwnProperty.call(element, key) ? element[key] : undefined;
  return typeof value === 'string' || (typeof value === 'number' && Number.isFinite(value)) ? JSON.stringify(value) : null;
}

/** Each element's key, or null where the element has none or another element shares it. */
function uniqueElementKeys(elements: readonly unknown[], key: string | undefined): Array<string | null> {
  const keys = elements.map((element) => elementKey(element, key));
  const seen = new Map<string, number>();
  for (const k of keys) if (k !== null) seen.set(k, (seen.get(k) ?? 0) + 1);
  return keys.map((k) => (k !== null && seen.get(k) === 1 ? k : null));
}

export type ResolvedTemplateBlock =
  | { type: 'card'; children: ResolvedTemplateBlock[] }
  | {
      type: 'row' | 'col';
      children: ResolvedTemplateBlock[];
      gap: number | null;
      align: (typeof TEMPLATE_ALIGNS)[number] | null;
      justify: (typeof TEMPLATE_JUSTIFIES)[number] | null;
    }
  | { type: 'list'; items: ResolvedTemplateBlock[]; direction: OneOf<typeof TEMPLATE_LIST_DIRECTIONS>; empty: string | null }
  | { type: 'image'; src: string; alt: string }
  | { type: 'title'; text: string; size: OneOf<typeof TEMPLATE_TITLE_SIZES> }
  | {
      type: 'text';
      text: string;
      size: OneOf<typeof TEMPLATE_TEXT_SIZES>;
      weight: OneOf<typeof TEMPLATE_TEXT_WEIGHTS>;
      color: TemplateTone;
    }
  | { type: 'badge'; text: string; color: TemplateTone; variant: OneOf<typeof TEMPLATE_BADGE_VARIANTS> }
  | { type: 'divider' }
  | { type: 'button'; label: string; style: OneOf<typeof TEMPLATE_BUTTON_STYLES>; action: TemplateActionAddress }
  | {
      type: 'select';
      name: string;
      label: string | null;
      options: Array<{ value: string; label: string }>;
      placeholder: string | null;
      default_value: string | null;
      required: boolean;
      source: string | null;
    }
  | {
      type: 'input';
      name: string;
      label: string | null;
      input_type: TemplateInputType;
      placeholder: string | null;
      default_value: string | null;
      required: boolean;
      source: string | null;
    }
  | {
      type: 'checkbox';
      name: string;
      label: string;
      default_checked: boolean;
      required: boolean;
      source: string | null;
    }
  | { type: 'form'; children: ResolvedTemplateBlock[]; submit: TemplateActionAddress; submit_label: string | null };

interface RenderState {
  /** Blocks this render may still resolve. */
  blocks: number;
  /** A block was left unresolved because `blocks` ran out. */
  overflowed: boolean;
  /** Binding steps this render may still take, shared by every text and value in it. */
  steps: BindingBudget;
}

/** A site in the view: `main`, then `.<child index>` into children or `.item` into a list's element. */
const SITE_ROOT = 'main';
const SITE_ITEM = 'item';
const childSite = (site: string, index: number) => `${site}.${index}`;
const itemSite = (site: string) => `${site}.${SITE_ITEM}`;

function optionalText(source: string | undefined, scope: BindingScope): string | null {
  if (source === undefined) return null;
  const value = renderBindingText(source, scope);
  return value === '' ? null : value;
}

function boundBoolean(value: boolean | string | undefined, scope: BindingScope): boolean {
  if (value === undefined) return false;
  if (typeof value === 'boolean') return value;
  return isBindingTruthy(resolveBindingValue(value, scope));
}

function resolveOptions(options: TemplateOption[] | string, scope: BindingScope): Array<{ value: string; label: string }> {
  const bound = typeof options === 'string';
  const raw = bound ? resolveBindingValue(options, scope) : options;
  if (!Array.isArray(raw)) return [];
  const out: Array<{ value: string; label: string }> = [];
  for (const entry of raw.slice(0, INTERACTIVE_TEMPLATE_OPTIONS_MAX)) {
    if (!isPlainRecord(entry)) continue;
    const { value, label } = entry;
    if (typeof value !== 'string' && typeof value !== 'number') continue;
    const valueText = bound ? String(value) : renderBindingText(String(value), scope);
    const labelText = typeof label === 'string' || typeof label === 'number'
      ? bound ? String(label) : renderBindingText(String(label), scope)
      : valueText;
    out.push({ value: valueText, label: labelText });
  }
  return out;
}

/** A field's common resolved props, or null when its name does not resolve to a valid one. */
function resolveField(
  block: { name: string; label?: string; required?: boolean | string; source?: string },
  scope: BindingScope,
): { name: string; label: string | null; required: boolean; source: string | null } | null {
  const name = renderBindingText(block.name, scope);
  if (!INTERACTIVE_TEMPLATE_FIELD_NAME_REGEX.test(name)) return null;
  return {
    name,
    label: optionalText(block.label, scope),
    required: boundBoolean(block.required, scope),
    source: optionalText(block.source, scope),
  };
}

function evaluateBindingPathText(source: string, scope: BindingScope): unknown {
  const parsed = parseBindingPath(source);
  return parsed.ok ? evaluateBindingPath(parsed.value, scope) : undefined;
}

function isShown(block: TemplateBlock, scope: BindingScope): boolean {
  return block.if === undefined || isBindingTruthy(evaluateBindingPathText(block.if, scope));
}

/** A list's elements as `each` reads them, or null past the nesting cap. Shared by both walkers. */
function listElements(block: TemplateBlock & { type: 'list' }, scope: BindingScope): unknown[] | null {
  if (scope.frames.length >= BINDING_EACH_MAX_DEPTH) return null;
  return bindingEachItems(evaluateBindingPathText(block.each, scope), block.limit);
}

function resolveBlock(
  block: TemplateBlock,
  site: string,
  frames: readonly BindingFrame[],
  keys: readonly (string | null)[],
  context: BindingContext,
  state: RenderState,
): ResolvedTemplateBlock | null {
  if (state.blocks <= 0) {
    state.overflowed = true;
    return null;
  }
  const scope = bindingScope(context, frames, state.steps);
  if (!isShown(block, scope)) return null;
  state.blocks -= 1;
  const address = (): TemplateActionAddress => ({ site, indices: frames.map((frame) => frame.index), keys: [...keys] });
  const kids = (list: TemplateBlock[]) =>
    list
      .map((child, i) => resolveBlock(child, childSite(site, i), frames, keys, context, state))
      .filter((child): child is ResolvedTemplateBlock => child !== null);
  switch (block.type) {
    case 'card':
      return { type: 'card', children: kids(block.children) };
    case 'row':
    case 'col':
      return {
        type: block.type,
        children: kids(block.children),
        gap: block.gap ?? null,
        align: block.align ?? null,
        justify: block.justify ?? null,
      };
    case 'form':
      return {
        type: 'form',
        children: kids(block.children),
        submit: address(),
        submit_label: optionalText(block.submit_label, scope),
      };
    case 'list': {
      const elements = listElements(block, scope);
      if (!elements) return null;
      const items: ResolvedTemplateBlock[] = [];
      const elementKeys = uniqueElementKeys(elements, block.key);
      elements.forEach((item, index) => {
        const resolved = resolveBlock(
          block.item,
          itemSite(site),
          [...frames, { item, index, length: elements.length }],
          [...keys, elementKeys[index] ?? null],
          context,
          state,
        );
        if (resolved) items.push(resolved);
      });
      return {
        type: 'list',
        items,
        direction: block.direction ?? 'vertical',
        empty: items.length === 0 ? optionalText(block.empty, scope) : null,
      };
    }
    case 'image': {
      const src = renderBindingText(block.src, scope).trim();
      if (!/^https:\/\/[^\s]+$/i.test(src)) return null;
      return { type: 'image', src, alt: optionalText(block.alt, scope) ?? '' };
    }
    case 'title':
      return { type: 'title', text: renderBindingText(block.text, scope), size: block.size ?? 'md' };
    case 'text':
      return {
        type: 'text',
        text: renderBindingText(block.text, scope),
        size: block.size ?? 'md',
        weight: block.weight ?? 'normal',
        color: block.color ?? 'default',
      };
    case 'badge':
      return {
        type: 'badge',
        text: renderBindingText(block.text, scope),
        color: block.color ?? 'secondary',
        variant: block.variant ?? 'soft',
      };
    case 'divider':
      return { type: 'divider' };
    case 'button':
      return {
        type: 'button',
        label: renderBindingText(block.label, scope),
        style: block.style ?? 'secondary',
        action: address(),
      };
    case 'select': {
      const field = resolveField(block, scope);
      if (!field) return null;
      return {
        type: 'select',
        ...field,
        options: resolveOptions(block.options, scope),
        placeholder: optionalText(block.placeholder, scope),
        default_value: optionalText(block.default_value, scope),
      };
    }
    case 'input': {
      const field = resolveField(block, scope);
      if (!field) return null;
      const type = block.input_type === undefined ? 'text' : resolveBindingValue(block.input_type, scope);
      return {
        type: 'input',
        ...field,
        input_type: (TEMPLATE_INPUT_TYPES as readonly unknown[]).includes(type) ? (type as TemplateInputType) : 'text',
        placeholder: optionalText(block.placeholder, scope),
        default_value: optionalText(block.default_value, scope),
      };
    }
    case 'checkbox': {
      const field = resolveField(block, scope);
      if (!field) return null;
      return {
        type: 'checkbox',
        ...field,
        label: renderBindingText(block.label, scope),
        default_checked: boundBoolean(block.default_checked, scope),
      };
    }
  }
}

function freshSteps(): BindingBudget {
  return { left: BINDING_RENDER_MAX_STEPS };
}

/**
 * The view as renderers draw it: the definition's `main` view resolved against a message's params
 * and data snapshot and the live state (RENDERING above). One render takes at most
 * `INTERACTIVE_TEMPLATE_RENDER_MAX_BLOCKS` blocks and `BINDING_RENDER_MAX_STEPS` binding steps across
 * all of them: a render that runs past either gives null, so the message shows its `message_text`
 * rather than half-filled text or a form missing fields beside working buttons. Null too when the
 * view itself is hidden or the definition cannot be read. Never throws.
 */
export function resolveInteractiveTemplateView(
  definition: InteractiveTemplateDefinition,
  context: BindingContext,
): ResolvedTemplateBlock | null {
  try {
    const state: RenderState = { blocks: INTERACTIVE_TEMPLATE_RENDER_MAX_BLOCKS, overflowed: false, steps: freshSteps() };
    const view = resolveBlock(definition.views.main, SITE_ROOT, [], [], context, state);
    return state.steps.left > 0 && !state.overflowed ? view : null;
  } catch {
    return null;
  }
}

/** A resolved action: what the server applies. `values` and `value` are the bindings' values. */
export type ResolvedTemplateAction =
  | { name: string; type: 'send'; text: string | null; values: unknown }
  | { name: string; type: 'set_state' | 'append_state' | 'remove_state'; path: string; value: unknown };

/**
 * Resolves an address (`TemplateActionAddress`) a client sent back: walks the stored definition to
 * the button or form at `site`, through each list at its element (by key where the list declares one,
 * else by index), checking every `if` on the way, and
 * resolves that block's action in its scope. `context.form` carries a form's submitted values (the
 * caller checks them against the form's fields first). Null when the address names no shown button
 * or form, or when resolving it spends `BINDING_RENDER_MAX_STEPS` (one budget for the walk and every
 * value). Never throws.
 */
export function resolveInteractiveTemplateAction(
  definition: InteractiveTemplateDefinition,
  address: TemplateActionAddress,
  context: BindingContext,
): ResolvedTemplateAction | null {
  try {
    const steps = address.site.split('.');
    if (steps.shift() !== SITE_ROOT || !Array.isArray(address.indices) || !Array.isArray(address.keys)) return null;
    if (address.keys.length !== address.indices.length) return null;
    const budget = freshSteps();
    let block: TemplateBlock = definition.views.main;
    const frames: BindingFrame[] = [];
    let used = 0;
    if (!isShown(block, bindingScope(context, frames, budget))) return null;
    for (const step of steps) {
      if (step === SITE_ITEM) {
        if (block.type !== 'list') return null;
        const hint = address.indices[used];
        const key = address.keys[used];
        used += 1;
        if (typeof hint !== 'number' || !Number.isInteger(hint) || hint < 0) return null;
        const elements = listElements(block, bindingScope(context, frames, budget));
        if (!elements) return null;
        let index = hint;
        if (block.key !== undefined) {
          if (typeof key !== 'string') return null;
          index = uniqueElementKeys(elements, block.key).indexOf(key);
          if (index < 0) return null;
        } else if (key !== null) {
          return null;
        }
        if (index >= elements.length) return null;
        frames.push({ item: elements[index], index, length: elements.length });
        block = block.item;
      } else {
        if (!/^\d{1,3}$/.test(step) || !('children' in block)) return null;
        const child: TemplateBlock | undefined = block.children[Number(step)];
        if (!child) return null;
        block = child;
      }
      if (!isShown(block, bindingScope(context, frames, budget))) return null;
    }
    if (used !== address.indices.length) return null;
    const name = block.type === 'button' ? block.action : block.type === 'form' ? block.submit : null;
    const action = name !== null ? definition.actions[name] : undefined;
    if (name === null || !action) return null;
    const scope = bindingScope(block.type === 'form' ? context : { ...context, form: undefined }, frames, budget);
    const resolved: ResolvedTemplateAction =
      action.type === 'send'
        ? {
            name,
            type: 'send',
            text: action.text === undefined ? null : renderBindingText(action.text, scope),
            values: action.values === undefined ? null : resolveBindingValue(action.values, scope) ?? null,
          }
        : { name, type: action.type, path: action.path, value: resolveBindingValue(action.value, scope) ?? null };
    return budget.left > 0 ? resolved : null;
  } catch {
    return null;
  }
}

/** The text a message's `message_text` takes: the fallback rendered, trimmed. Never throws. */
export function renderInteractiveTemplateFallback(
  definition: InteractiveTemplateDefinition,
  context: BindingContext,
): string {
  return renderBindingText(definition.fallback, bindingScope(context)).trim();
}

/**
 * The values the backend calls the data tool with: `data.params` resolved over the message's params
 * and the state. Null when the definition declares no data. Never throws.
 */
export function resolveInteractiveTemplateDataParams(
  definition: InteractiveTemplateDefinition,
  context: Pick<BindingContext, 'params' | 'state'>,
): Record<string, unknown> | null {
  if (!definition.data) return null;
  const resolved = resolveBindingValue(definition.data.params, bindingScope({ params: context.params, state: context.state }));
  return isPlainRecord(resolved) ? resolved : {};
}
